import { Stack, Typography } from "@mui/material";
import React, { ReactNode, useCallback, useMemo, useState } from "react";
import Joyride, { CallBackProps, STATUS, Step } from "react-joyride";
import CustomTooltip from "../../Components/CustomTooltip";
import { useNavigate } from "react-router";

interface CustomStep extends Step {
  _tutorialTitle: string;
  switch?: boolean;
  to?: string;
}
interface StepInfo {
  title: string;
  titleIcon?: ReactNode;
  _tutorialTitle?: string;
  switch?: boolean;
  to?: string;
  content: string;
  target: string;
  placement?: Step["placement"];
}

type JoyRideReturn = [
  ReactNode,
  {
    start: () => void;
    stop: () => void;
  },
  {
    isRunning: boolean;
    stepIndex: number;
    tutorialTitle: string;
  }
];

export const useJoyride = (stepsInfos: StepInfo[]): JoyRideReturn => {
  const [isRunning, setIsRunning] = useState(false);
  const navigate = useNavigate();
  const steps = useMemo(
    (): CustomStep[] =>
      stepsInfos.map((stepInfos, index) => ({
        ...stepInfos,
        _tutorialTitle: stepInfos._tutorialTitle || "",
        switch: stepInfos.switch || false,
        to: stepInfos.to || "",
        disableBeacon: true,
        title: (
          <Stack mb={1} direction="row" alignItems="center" color="primary.contrastText">
            {stepInfos.titleIcon && stepInfos.titleIcon}
            <Typography sx={{ ml: stepInfos.titleIcon ? 0.5 : 0 }} fontWeight={600} variant="subtitle2">
              {stepInfos.title}
            </Typography>
          </Stack>
        ),
        content: (
          <Typography mb={2} color="primary.contrastText">
            {stepInfos.content}
          </Typography>
        ),
      })),
    [stepsInfos]
  );

  const [stepIndex, setStepIndex] = React.useState<number>(0);
  const [tutorialTitle, setTutorialTitle] = React.useState<string>("");

  const startTutorial = useCallback(() => {
    setIsRunning(true);
  }, []);

  const stopTutorial = useCallback(() => {
    setIsRunning(false);
  }, []);

  const handleCallback = useCallback((data: CallBackProps & { step: CustomStep }) => {
    const { status, step } = data;

    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status as any)) {
      setIsRunning(false);
    }

    if ([STATUS.RUNNING].includes(status as any)) {
      setStepIndex(data.index);
      setTutorialTitle(step._tutorialTitle);
    }

    if (step.switch && step.to && step.to.length > 0) navigate(step.to);
  }, []);

  const JoyRideComponent = useMemo(
    () => (
      <Joyride
        // disableOverlay
        continuous
        // disableOverlayClose
        scrollDuration={100}
        scrollOffset={200}
        // @ts-ignore : custom step index is passed
        callback={handleCallback}
        tooltipComponent={CustomTooltip}
        styles={{ options: { zIndex: 10000 } }}
        steps={steps}
        run={isRunning}
      />
    ),
    [steps, isRunning, handleCallback]
  );

  const actions = React.useMemo(
    () => ({
      start: startTutorial,
      stop: stopTutorial,
    }),
    [startTutorial, stopTutorial]
  );
  const state = React.useMemo(
    () => ({
      tutorialTitle,
      stepIndex,
      isRunning,
    }),
    [isRunning, tutorialTitle, stepIndex]
  );

  return [JoyRideComponent, actions, state];
};
